import styled from "@emotion/styled";
import {observer } from "mobx-react-lite";
import {waitForTx} from "@waves/waves-transactions";
import {useEffect, useState} from "react";
import {useStores} from "src/stores/useStores";
import CardStore, { NftType } from "src/stores/CardStore";

import Scrollbar from '../Scrollbar/Scrollbar';
import Button from './Button';

import {NODE_URL_MAP} from "src/constants";
import {getDaoNFTInfo, PuzzleDragonsSignArtIds} from "./Data";
import { Link } from "react-router-dom";
import Skeleton from "react-loading-skeleton";

interface IDragonListProps {
    onUpdated?: () => void;
}

const MAINNET_ADDRESS = NODE_URL_MAP.W;
const DaoAddress = '3P975LUuqc7uBPiSxp3nemf2FZh62zfirSh';

const Root = styled.div`
    overflow: hidden;
`;

const List = styled.div`
    display: grid;
    column-gap: 12px;
    row-gap: 24px;
    grid-template-columns: 40px minmax(0, 1fr) 80px;
    align-items: center;
    box-sizing: border-box;
    width: 100%;
`;

const Row = styled.div`
    display: contents;
`;

const Image = styled.img`
    width: 40px;
    height: 40px;
    background: #D9D9D9;
    border-radius: 10px;
`;

const InfoBlock = styled.div`
    display: flex;
    flex-direction: column;
`;

const ItemTitle = styled.div`
    font-weight: 500;
    font-size: 16px;
    line-height: 24px;
    color: #363870;
`;

const ItemDesc = styled.div`
    font-weight: 400;
    font-size: 14px;
    line-height: 18px;
    color: #8082C5;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

const EmptyText = styled.div`
    font-size: 14px;
    font-weight: 400;
    color: #363870;
    line-height: 20px;
`;

const BuyLink = styled(Link)`
    color: #7075E9;
    font-weight: 500;
`

export default observer(function(props: IDragonListProps) {
    const rootStore = useStores();
    const [updated, setUpdated] = useState<number>(0);
    const [userNFTs, setUserNFTs] = useState<CardStore[]>([]);
    const [daoNFTs, setDaoNFTs] = useState<CardStore[]>([]);
    const [signArtDragonsNFTs, setSignArtDragonsNFTs] = useState<CardStore[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(false);

    useEffect(() => {
        const fetchData = async () => {
            try {
                setIsLoading(true);
                const userNfts = await rootStore.walletStore.loadUserMarketNfts(rootStore.accountStore.address, true);
                const daoNftInfo = await getDaoNFTInfo();

                const rawDaoNfts = await rootStore.walletStore.loadAddressNFT(DaoAddress, true);
                const daoDataNfts = await rootStore.walletStore.loadMarketNfts(rawDaoNfts);
                daoDataNfts
                    .forEach((item) => {
                        if (daoNftInfo?.length) {
                            daoNftInfo.forEach((dataItem) => {
                                const [name, assetId, key] = dataItem.key.split('_');
                                if (name === 'nft' && assetId === item.assetId && key === 'owner') {
                                    item.owner = dataItem.value as string;
                                }
                            });
                        }
                    });
                const daoSingArtDragons = await rootStore.walletStore.loadUserSignArtNfts(DaoAddress, PuzzleDragonsSignArtIds);
                daoDataNfts.forEach(item => {
                    const daoSignArtDragon = daoSingArtDragons.filter(second => second.assetId === item.assetId);
                    if (daoSignArtDragon.length) {
                        item.name = daoSignArtDragon[0].name;
                        item.image = daoSignArtDragon[0].image;
                        item.description = daoSignArtDragon[0].description;
                        item.type = NftType.SIGN_ART;
                    }
                })

                const singArtDragons = await rootStore.walletStore.loadUserSignArtNfts(rootStore.accountStore.address, PuzzleDragonsSignArtIds);
                setUserNFTs(userNfts.filter(item => item.creator === DaoAddress));
                setDaoNFTs(daoDataNfts);
                setSignArtDragonsNFTs(singArtDragons);
                props.onUpdated?.();
            } catch (e: any) {
                rootStore.notificationStore.notify(e.message ?? JSON.stringify(e), {
                    type: 'error',
                    title: 'Transaction is not completed'
                });
            } finally {
                setIsLoading(false);
            }
        }
        fetchData();
    }, [updated, rootStore.accountStore.address]);

    const clickAction = async (item: CardStore, stake: boolean = true) => {
        try {
            const txId = await rootStore.accountStore.invoke({
                dApp: DaoAddress,
                payment: stake ? [
                    {assetId: item.assetId, amount: 1}
                ] : [],
                call: {
                    function: stake ? 'stake' : 'unstake',
                    args: stake ? [{type: 'boolean', value: false}] : [
                        {type: 'string', value: item.assetId},
                        {type: 'boolean', value: false}
                    ]
                }
            });
            await waitForTx(txId as string, { apiBase: MAINNET_ADDRESS });
            if (txId) {
                rootStore.notificationStore.notify(`Your NFT staked`, {
                    type: "success",
                    title: `Success`,
                    link: `https://wavesexplorer.com/tx/${txId}`,
                    linkTitle: "View on Explorer",
                });
            }
        } catch(e: any) {
            console.error(e);
            rootStore.notificationStore.notify(e.message ?? JSON.stringify(e), {
                type: 'error',
                title: 'Transaction is not completed'
            });
        } finally {
            setUpdated(updated + 1);
        }
    };

    const daoUserDragons = daoNFTs?.filter((item) => item.owner === rootStore.accountStore.address) || [];

    return (
        <Root>
            {
                !isLoading && !daoUserDragons.length && !userNFTs.length ?
                    <EmptyText>You don't have dragons. <BuyLink to="/collection/3P975LUuqc7uBPiSxp3nemf2FZh62zfirSh/Market DAO Dragons">Buy here</BuyLink></EmptyText> : ''
            }
            <Scrollbar>
                <List>
                    {
                        isLoading ?
                            [1,2].map((item, index) => {
                                return <Row key={index}>
                                        <Skeleton width={40} height={40}/>
                                        <InfoBlock>
                                            <Skeleton width='100%' height={22}/>
                                            <Skeleton width={80} height={16}/>
                                        </InfoBlock>
                                        <Skeleton width={80} height={32}/>
                                    </Row>
                            }) : ''
                    }
                    {!isLoading && daoUserDragons
                        .map((item) => {
                            return <Row key={item.assetId}>
                                    <Link to={item.marketLink}>
                                        <Image src={item.imageUrl}/>
                                    </Link>
                                    <InfoBlock>
                                        <ItemTitle>{item.name}</ItemTitle>
                                        <ItemDesc>{item.description}</ItemDesc>
                                    </InfoBlock>
                                    <Button
                                        caption='Unstake'
                                        size="small"
                                        viewMode='secondary'
                                        onClick={() => {
                                            clickAction(item, false);
                                        }}/>
                                </Row>
                        })}
                    {!isLoading && signArtDragonsNFTs
                        .map((item) => {
                            return <Row key={item.assetId}>
                                    <Link to={item.marketLink}>
                                        <Image src={item.imageUrl}/>
                                    </Link>
                                    <InfoBlock>
                                        <ItemTitle>{item.name}</ItemTitle>
                                        <ItemDesc>{item.description}</ItemDesc>
                                    </InfoBlock>
                                    <Button
                                        caption='Stake'
                                        size="small"
                                        viewMode='primary'
                                        onClick={() => {
                                            clickAction(item);
                                        }}/>
                                </Row>
                        })}
                    {!isLoading && userNFTs
                        .map((item) => {
                            return <Row key={item.assetId}>
                                    <Link to={item.marketLink}>
                                        <Image src={item.imageUrl}/>
                                    </Link>
                                    <InfoBlock>
                                        <ItemTitle>{item.name}</ItemTitle>
                                        <ItemDesc>{item.description}</ItemDesc>
                                    </InfoBlock>
                                    <Button
                                        caption='Stake'
                                        size="small"
                                        viewMode='primary'
                                        onClick={() => {
                                            clickAction(item);
                                        }}/>
                                </Row>
                        })}
                </List>
            </Scrollbar>
        </Root>
    );
});